import { SwapOutlined } from "@ant-design/icons";
import { Space, Tooltip } from "antd";
import ErrorBoundary from "components/error-boundary";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import ActivityIcon from "components/svg-icons/activity-icon";
import { TLinkSource } from "components/feature-entity-linking-page/models";
import { useState } from "react";
import { DateUtils } from "utils/dateutils";
import AIPoweredIcon from "components/svg-icons/ai-powered-icon";
import ActivityCardSetting from "./activity-card-setting";
import OffsetDurationInputBox from "./offset-duration-input-box";

function FeatureRowActivityCard({
  item,
  isEditing,
  onRemoveRow,
  isAllowSetting,
  onOffsetChange,
  updateReverseLinking
}: {
  item: TLinkSource;
  isEditing: boolean;
  onRemoveRow: (rowId: string) => void;
  isAllowSetting: boolean;
  onOffsetChange: (prop: {
    offset: number | null;
    activityLink: TLinkSource;
  }) => void;
  updateReverseLinking: (prop: { activityLink: TLinkSource }) => void;
}) {
  const [open, setOpen] = useState(false);

  const task = item.feature_instance;
  if (!task) return null;

  const taskData = task?.feature_activities![0];

  const isPermissionEnabled = taskData?.total_slack_computed !== "restricted";

  const canEdit = true; // Assuming canEdit is always true for this example

  return (
    <div className="flex border border-color bg-white rounded overflow-hidden mb-1">
      {item.recommended_by_system && (
        <div
          className="flex flex-col w-7 items-center justify-center rounded-l bg-ciq-golden-purple-1 border-0 border-r border-solid border-ciq-golden-purple-3"
          data-testid="ai-powered-indicator"
        >
          <AIPoweredIcon />
        </div>
      )}
      <div className="flex flex-col justify-center p-2 features-card w-full">
        <div className="flex items-center justify-evenly mb-2 w-full">
          {isPermissionEnabled && taskData?.critical ? (
            <div className="ml-[2px]">
              <CriticalActivityIndicator />
            </div>
          ) : (
            <ActivityIcon
              size={20}
              fill="var(--ciq-gray-480)"
              className="grow-none"
              data-testid="activity-icon"
            />
          )}

          <i
            className="grow-none entity-id-with-spec pr-1"
            data-testid="task-number"
          >
            {task?.number}
          </i>
          <Tooltip
            title={task.title}
            className="flex align-center grow"
            placement="topLeft"
            data-testid="task-title-tooltip"
          >
            {item.governing_link ? (
              <span
                className="text-base mr-1 pt-[10px] leading-[0]"
                data-testid="governing-link-indicator"
              >
                *
              </span>
            ) : (
              ""
            )}
            <span data-testid="task-title">{task.title}</span>{" "}
            {taskData?.start_date && item.isLinkedWithStartdate && (
              <i className="entity-id-with-spec" data-testid="start-date">
                Start Date :{" "}
                {taskData?.start_date
                  ? DateUtils.format(taskData?.start_date)
                  : ""}
              </i>
            )}
            {taskData?.end_date && !item.isLinkedWithStartdate && (
              <i className="entity-id-with-spec" data-testid="end-date">
                End Date :{" "}
                {taskData?.end_date ? DateUtils.format(taskData?.end_date) : ""}
              </i>
            )}
          </Tooltip>
          {isEditing && (
            <div className="flex justify-between items-center">
              <Space
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  onRemoveRow(task.id);
                }}
                className="unlink-button grow-none"
                data-testid="unlink-button"
              >
                Unlink
              </Space>
              {isAllowSetting && (
                <ActivityCardSetting
                  activityLinkedItem={item}
                  openSettings={open}
                  setOpenSettings={setOpen}
                  onOffsetChange={onOffsetChange}
                  updateReverseLinking={updateReverseLinking}
                />
              )}
            </div>
          )}
        </div>
        {item.reverse_dependency && (
          <div className="flex items-center space-x-2 py-1 pt-2">
            {item.reverse_dependency && (
              <div className="flex " data-testid="predecessor-indicator">
                <Space
                  className="px-4 py-1 flex bg-ciq-gray-500 rounded-sm h-[32px]"
                  data-testid="predecessor-badge"
                >
                  <SwapOutlined
                    className="text-ciq-gray-550"
                    data-testid="swap-icon"
                  />{" "}
                  Activity is the Predecessor
                </Space>
              </div>
            )}
          </div>
        )}
        {(item.offset_duration || open) && (
          <ErrorBoundary>
            <div className="flex py-1">
              <OffsetDurationInputBox
                task={item}
                onOffsetChange={(params) => {
                  setOpen(false);
                  onOffsetChange(params);
                }}
                canEdit={canEdit && isEditing}
                openToEdit={open && isEditing}
                data-testid="offset-duration-input"
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
}

export default FeatureRowActivityCard;
